import React, { useContext, useEffect, useMemo, useState } from 'react';
import { EntityValidations, ISpecificEntityFormProps, IApp03Project, App03Project, CustomField, Contributor } from '../../types';
import { DateService, LookupService } from '../../services';
import styles from '../../styles/cr.module.scss';
import { CrUserPicker } from '../cr/CrUserPicker';
import { CrTextField } from '../cr/CrTextField';
import { CrDropdown } from '../cr/CrDropdown';
import { EntityForm } from '../EntityForm';
import { DataContext } from '../DataContext';
import { IntegrationContext } from '../IntegrationContext';
import { App03Frameworks } from '../../types/App03Workflow';
import { CrDatePicker } from '../cr/CrDatePicker';
import { CrCustomFields } from '../cr/CrCustomFields';
import { AppIDs, getFilteredCustomFieldDfns } from '../../AppGlobals';
import { PortfolioTypes } from '../../types/App03Portfolio';

class ProjectValidations extends EntityValidations {
    public Owners: string = null;
    public Portfolio: string = null;
    public ProjectType: string = null;
    public ProjectStage: string = null;
    public StartDate: string = null;
    public EndDate: string = null;
}

interface WFProjectFormProps extends ISpecificEntityFormProps {
    frameworkID: App03Frameworks;
}

export const WFProjectForm = (props: WFProjectFormProps): React.ReactElement => {
    const { disableGroupManagement } = useContext(IntegrationContext);
    const { dataServices, lookupData, loadLookupData: { app03Portfolios, app03ProjectTypes, projectStages, customFieldDfns, entityStatuses, users: { all: allUsers } } } = useContext(DataContext);

    useMemo(() => app03Portfolios(), [app03Portfolios]);
    useMemo(() => app03ProjectTypes(), [app03ProjectTypes]);
    useMemo(() => projectStages(), [projectStages]);
    useMemo(() => customFieldDfns(), [customFieldDfns]);
    useMemo(() => entityStatuses(), [entityStatuses]);
    useMemo(() => allUsers(), [allUsers]);

    const isInvalidId = (value: any): boolean =>
        value === null ||
        value === undefined ||
        value === "" ||
        isNaN(Number(value));

    const validateEntity = (project: IApp03Project): Promise<ProjectValidations> => {
        const errors = new ProjectValidations();

        if (project.Title === null || project.Title === '') {
            errors.Title = `Project name is required`;
            errors.Valid = false;
        }
        else {
            errors.Title = null;
        }
        if (isInvalidId(project.PortfolioID)) {
            errors.Portfolio = "Portfolio is required";
            errors.Valid = false;
        } else {
            errors.Portfolio = null;
        }
        if (isInvalidId(project.ProjectTypeID)) {
            errors.ProjectType = "Category is required";
            errors.Valid = false;
        } else {
            errors.ProjectType = null;
        }
        if (isInvalidId(project.ProjectStageID)) {
            errors.ProjectStage = "Stage is required";
            errors.Valid = false;
        } else {
            errors.ProjectStage = null;
        }

        // Start Date required
        if (!project.StartDate) {
            errors.StartDate = 'Start Date is required';
            errors.Valid = false;
        } else {
            errors.StartDate = null;
        }

        // Target End Date required
        if (!project.EndDate) {
            errors.EndDate = 'End Date is required';
            errors.Valid = false;
        } else {
            errors.EndDate = null;
        }

        // End date cannot be earlier than start date
        if (
            project.StartDate &&
            project.EndDate &&
            project.EndDate < project.StartDate
        ) {
            errors.EndDate = 'End date cannot be earlier than start date';
            errors.Valid = false;
        }


        // if (project.App03PortfolioUsers === undefined || project.App03PortfolioUsers === null || project.App03PortfolioUsers.length < 1) {
        //     errors.Owners = `At least one owner is required`;
        //     errors.Valid = false;
        // }
        // else {
        //     errors.Owners = null;
        // }

        return Promise.resolve(errors);
    };
    const users = lookupData.Users && lookupData.Users.All;
    const filteredCustomFieldDfns = useMemo(() => {
        return getFilteredCustomFieldDfns(
            lookupData.CustomFieldDfns,
            [
                'All Apps - Common',
                'All Delivery Apps - Projects',
            ]
        );
    }, [lookupData.CustomFieldDfns]);

    return (
        <EntityForm<IApp03Project, ProjectValidations>
            {...props}
            entityName='Project'
            renderFormFields={(changeHandlers, formState) => {
                const { FormData: project, ValidationErrors: errors } = formState;
                return (
                    <div>
                        <CrTextField
                            label="Name"
                            maxLength={100}
                            className={styles.formField}
                            required={true}
                            value={project.Title}
                            onChange={(ev, newValue) => changeHandlers.changeTextField(newValue, 'Title')}
                            errorMessage={errors.Title}
                        />

                        <CrTextField
                            label="Project Objective"
                            multiline
                            rows={6}
                            maxLength={10000}
                            className={styles.formField}
                            value={project.Objectives}
                            onChange={(ev, newValue) => changeHandlers.changeTextField(newValue, 'Objectives')}
                        />
                        {!(props.showEntityDescription1 === true/* || (wf.PPProjectID > 0)*/) && (
                            <CrDropdown
                                label="Portfolio"
                                className={styles.formField}
                                options={LookupService.entitiesToSelectableOptions(
                                    (lookupData.App03Portfolios ?? []).filter(
                                        p => Number(p.FrameworkID) === Number(props.frameworkID)
                                    )
                                )}
                                selectedKey={project.PortfolioID}
                                onChange={(_, o) => changeHandlers.changeDropdown(o, 'PortfolioID')}
                                required={true}
                                errorMessage={errors.Portfolio}

                            />
                        )}

                        <CrDropdown
                            label="Category"
                            className={styles.formField}
                            options={LookupService.entitiesToSelectableOptions(lookupData.App03ProjectTypes)}
                            selectedKey={project.ProjectTypeID}
                            onChange={(_, o) => changeHandlers.changeDropdown(o, 'ProjectTypeID')}
                            required={true}
                            errorMessage={errors.ProjectType}
                        />

                        <CrDropdown
                            label='Stage'
                            className={styles.formField}
                            options={LookupService.entitiesToSelectableOptions(lookupData.ProjectStages)}
                            selectedKey={project.ProjectStageID}
                            onChange={(_, v) => changeHandlers.changeDropdown(v, 'ProjectStageID')}
                            required={true}
                            errorMessage={errors.ProjectStage}
                        />

                        <CrDatePicker
                            label="Start date"
                            className={styles.formField}
                            //disabled={disableEdit}
                            value={DateService.removeTimezoneOffset(project.StartDate)}
                            onSelectDate={date => changeHandlers.changeDatePicker(date, 'StartDate')}
                            required={true}
                            errorMessage={errors.StartDate}
                        />
                        <CrDatePicker
                            label="End date"
                            className={styles.formField}
                            //disabled={disableEdit}
                            value={DateService.removeTimezoneOffset(project.EndDate)}
                            minDate={DateService.removeTimezoneOffset(project.StartDate)}
                            onSelectDate={date => changeHandlers.changeDatePicker(date, 'EndDate')}
                            required={true}
                            errorMessage={errors.EndDate}
                        />

                        <CrUserPicker
                            label="Owner"
                            className={styles.formField}
                            users={lookupData.Users?.All}
                            selectedUsers={project.OwnerUserID && [project.OwnerUserID]}
                            onChange={u => changeHandlers.changeUserPicker(u, 'OwnerUserID')}
                        />

                        <CrUserPicker
                            label="Contributors"
                            className={styles.formField}
                            users={users}
                            itemLimit={3}
                            selectedUsers={project.Contributors?.map(c => c.ContributorUserID)}
                            onChange={v => changeHandlers.changeMultiUserPicker(v, 'Contributors', new Contributor(), 'ContributorUserID')}
                        //{...suggestions}
                        />

                        <CrCustomFields
                            label="Additional Information"
                            className={styles.formField}
                            customFieldDfns={filteredCustomFieldDfns}
                            selectedItems={LookupService.customFieldsToDropdownWithText(project.CustomFields)}
                            onChange={v => changeHandlers.changeMultiDropdownWithText(v, 'CustomFields', new CustomField(), 'CustomFieldDefnID', 'CustomFieldValue')}
                        />

                        <CrDropdown
                            label="Status"
                            className={styles.formField}
                            disabled={disableGroupManagement}
                            options={LookupService.entitiesToSelectableOptions(lookupData.EntityStatuses)}
                            selectedKey={project.EntityStatusID}
                            onChange={(_, o) => changeHandlers.changeStatusDropdown(o, 'EntityStatusID')}
                        />

                    </div>
                );
            }}
            loadEntity={id => dataServices.app03ProjectService.read(id, true, true)}
            loadNewEntity={() => new App03Project(PortfolioTypes.General, props.frameworkID)}
            loadEntityValidations={() => new ProjectValidations()}
            onValidateEntity={validateEntity}
            onCreate={p => dataServices.app03ProjectService.create(p)}
            onUpdate={p => dataServices.app03ProjectService.update(p.ID, p)}
            parentEntities={dataServices.app03ProjectService.parentEntities}
            childEntities={[
                {
                    ObjectParentProperty: 'Contributors',
                    ParentIdProperty: 'App03ProjectID',
                    ChildIdProperty: 'ContributorUserID',
                    ChildService: dataServices.contributorService
                },
                {
                    ObjectParentProperty: 'CustomFields',
                    ParentIdProperty: 'App03ProjectID',
                    ChildIdProperty: 'CustomFieldDefnID',
                    ChildService: dataServices.customFieldService
                },
            ]}
        />
    );
};
